import { useEffect, useMemo, useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'

import {
  FEATURE_TOGGLE_SELLER_CENTRAL,
  useFeatureToggle,
} from '../../feature-toggles'
import { useGlobalConfigContext } from '../../global-config'
import { useMarketplacePartnerRegisterPageContext } from '../components/marketplacePartnerRegisterPageContext'

import { useMarketplacePartnerRegisterPageGetDictionaryByName } from './useMarketplacePartnerRegisterPageGetDictionaryByName'
import {
  type MarketplacePartnerRegisterPageDictionaryOption,
  useMarketplacePartnerRegisterPageGetDictionaryOptionsByName,
} from './useMarketplacePartnerRegisterPageGetDictionaryOptionsByName'

type CategorySelectionEntry = {
  isChecked: boolean
  isSelected: boolean
  key: string
  label: string
}

// For now we use both static list of categories translated in lokalise and fetched dictionary from the backend.
// Once seller central feature is released to production we will remove static list and use only fetched dictionary. WSCR-3860
const categoryMessages = defineMessages({
  animal: { id: 'marketplacePartnerRegisterPage.categoriesSelection.animal' },
  baby: { id: 'marketplacePartnerRegisterPage.categoriesSelection.baby' },
  beauty: { id: 'marketplacePartnerRegisterPage.categoriesSelection.beauty' },
  books: { id: 'marketplacePartnerRegisterPage.categoriesSelection.books' },
  glasses: { id: 'marketplacePartnerRegisterPage.categoriesSelection.glasses' },
  household: {
    id: 'marketplacePartnerRegisterPage.categoriesSelection.household',
  },
  medicalSupplies: {
    id: 'marketplacePartnerRegisterPage.categoriesSelection.medicalSupplies',
  },
  nutrition: {
    id: 'marketplacePartnerRegisterPage.categoriesSelection.nutrition',
  },
  partnership: {
    id: 'marketplacePartnerRegisterPage.categoriesSelection.partnership',
  },
  sport: { id: 'marketplacePartnerRegisterPage.categoriesSelection.sport' },
})

const useAllAvailableCategories = (): {
  allAvailable: MarketplacePartnerRegisterPageDictionaryOption[]
} => {
  const intl = useIntl()
  const isSellerCentralFeatureEnabled = useFeatureToggle(
    FEATURE_TOGGLE_SELLER_CENTRAL,
  )
  const { language } = useGlobalConfigContext()

  const sellerCentralCategoriesOptions =
    useMarketplacePartnerRegisterPageGetDictionaryOptionsByName(
      'categories',
      language,
    )
  const sellerCentralCategoriesLabels =
    useMarketplacePartnerRegisterPageGetDictionaryByName('categories', language)

  const allAvailable = useMemo(() => {
    if (isSellerCentralFeatureEnabled && sellerCentralCategoriesOptions) {
      return sellerCentralCategoriesOptions
    }

    if (isSellerCentralFeatureEnabled && sellerCentralCategoriesLabels) {
      return sellerCentralCategoriesLabels.map((label) => ({
        key: label,
        label,
      }))
    }

    return Object.values(categoryMessages).map((category) => {
      const label = intl.formatMessage(category)

      return {
        key: label,
        label,
      }
    })
  }, [
    intl,
    isSellerCentralFeatureEnabled,
    sellerCentralCategoriesLabels,
    sellerCentralCategoriesOptions,
  ])

  return { allAvailable }
}

export const useMarketplacePartnerRegisterPageFormCategoriesSelection = (): {
  availableCategories: CategorySelectionEntry[]
  categoriesSelectionEntries: CategorySelectionEntry[]
  handleCheck: (
    categoryValue: string,
    checked: boolean,
    isAvailable: boolean,
  ) => void
  moveCategories: (toSelected: boolean) => void
  selectedCategories: CategorySelectionEntry[]
} => {
  const { formState, resetVersion } = useMarketplacePartnerRegisterPageContext()
  const { model } = formState
  formState.use()
  const { allAvailable } = useAllAvailableCategories()

  const defaultCategories = useMemo(
    () =>
      allAvailable.map((category) => ({
        isChecked: false,
        isSelected: false,
        key: category.key,
        label: category.label,
      })),
    [allAvailable],
  )

  const [categoriesSelectionEntries, setCategoriesSelectionEntries] =
    useState<CategorySelectionEntry[]>(defaultCategories)

  const handleCheck = (
    categoryKey: string,
    checked: boolean,
    isAvailable: boolean,
  ): void => {
    setCategoriesSelectionEntries((prev) =>
      prev.map((entry) =>
        entry.key === categoryKey && entry.isSelected === !isAvailable
          ? { ...entry, isChecked: checked }
          : entry,
      ),
    )
  }

  const moveCategories = (toSelected: boolean): void => {
    setCategoriesSelectionEntries((prev) =>
      prev.map((entry) => {
        if (entry.isChecked && entry.isSelected !== toSelected) {
          return { ...entry, isChecked: false, isSelected: toSelected }
        }

        return entry
      }),
    )
  }

  useEffect(() => {
    const prev = model.categories
    const sameLength =
      categoriesSelectionEntries.filter((entry) => entry.isSelected).length ===
      prev.length
    const isSame =
      sameLength &&
      categoriesSelectionEntries
        .filter((entry) => entry.isSelected)
        .every((entry, idx) => entry.key === prev[idx])

    if (isSame) {
      return
    }

    formState.setValue(
      categoriesSelectionEntries
        .filter((entry) => entry.isSelected)
        .map((entry) => entry.key),
      model,
      'categories',
    )
    model.validator.validate('categories')
  }, [categoriesSelectionEntries, formState, model])

  const availableCategories = categoriesSelectionEntries.filter(
    (entry) => !entry.isSelected,
  )

  const selectedCategories = categoriesSelectionEntries.filter(
    (entry) => entry.isSelected,
  )

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- don't reference, refactor if touched
    setCategoriesSelectionEntries(defaultCategories)
  }, [defaultCategories, resetVersion])

  return {
    availableCategories,
    categoriesSelectionEntries,
    handleCheck,
    moveCategories,
    selectedCategories,
  }
}
